/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Organization and Supabase client context – provider and useOrganization hook.
 */

"use client";

import { useSessionSafe } from "@/hooks/auth/use-session-safe";
import { useUserSafe } from "@/hooks/auth/use-user-safe";
import { clearTokenCache } from "@/lib/auth/token-utils";
import { clearThemeFromDOM } from "@/components/providers/theme-provider/utils/theme-storage";
import { clearOrgThemeCookie } from "@/lib/utils/design/theme-cookies";
import { clearOnboardingDraft } from "@/lib/utils/domain/onboarding-state";
import { clearCountryCache } from "@/contexts/use-organization-country";
import { createClerkSupabaseClient } from "@/lib/supabase/clerk-client";
import { logger } from "@/lib/utils/logger";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getOrgFromCache,
  setOrgInCache,
  clearOrgCache,
} from "./org-data-cache";
import { useOrganizationCountry } from "./use-organization-country";
import { DEFAULT_TEST_USERS } from "@/lib/testing/test-mode-simple";

interface OrganizationContextType {
  supabase: ReturnType<typeof createClerkSupabaseClient> | null;
  organizationId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  userId: string | null;
  isClientReady: boolean;
  organizationCountry: string | null;
}

// PERF: Split into two contexts to reduce re-render blast radius.
// OrgIdentityContext holds stable org data (changes only on org switch).
// OrgClientContext holds the Supabase client (changes on token refresh).
// useOrganization() merges both for backward compatibility — no consumer changes needed.
// Components that only need organizationId (85+ consumers) can use useOrgIdentity()
// to avoid re-renders when the Supabase client refreshes its token.

interface OrgIdentityContextType {
  organizationId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  userId: string | null;
  organizationCountry: string | null;
}

interface OrgClientContextType {
  supabase: ReturnType<typeof createClerkSupabaseClient> | null;
  isClientReady: boolean;
}

const OrgIdentityContext = createContext<OrgIdentityContextType | null>(null);
const OrgClientContext = createContext<OrgClientContextType | null>(null);

// Legacy context kept for any direct consumers (should migrate to split contexts)
const OrganizationContext = createContext<OrganizationContextType | null>(null);

// Internal provider that uses Clerk hooks
function OrganizationProviderInternal({
  children,
  isTestMode,
  testOrgId,
}: {
  children: React.ReactNode;
  isTestMode: boolean;
  testOrgId: string | null;
}) {
  // Use safe wrappers that handle test mode internally
  const { session } = useSessionSafe();
  const { user } = useUserSafe();

  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isClientReady, setIsClientReady] = useState(false);
  const [supabase, setSupabase] = useState<ReturnType<
    typeof createClerkSupabaseClient
  > | null>(null);
  const clientInitializedRef = useRef<boolean>(false);
  const clientCreationInFlightRef = useRef<boolean>(false);
  const lastTokenRef = useRef<string | null>(null);

  // Track if we've already fetched organization data
  const hasFetchedOrg = useRef(false);

  // Track if we've ever had a valid session — prevents clearing caches on initial mount
  // when Clerk hooks haven't loaded yet (reports null before hydrating)
  const hadSessionRef = useRef(false);

  const sessionId = session?.id ?? null;
  const userId = user?.id ?? null;

  const organizationCountry = useOrganizationCountry(
    organizationId,
    session ?? null,
    isTestMode,
  );

  // Early return for test mode - bypass all Clerk logic
  useEffect(() => {
    if (isTestMode) {
      logger.info("🧪 [ORG CONTEXT] Test mode activated, bypassing Clerk auth");
      setOrganizationId(testOrgId);
      setIsLoading(false);
      setIsClientReady(true);

      // Create a mock Supabase client for test mode
      try {
        const mockClient = createClerkSupabaseClient({
          accessToken: "test-token",
        });
        setSupabase(mockClient);
      } catch (error) {
        logger.warn(
          "🧪 [ORG CONTEXT] Could not create test Supabase client:",
          error,
        );
      }

      return;
    }
  }, [isTestMode, testOrgId]);

  // Create Supabase client using Clerk session (browser singleton)
  useEffect(() => {
    if (isTestMode) return; // Skip in test mode

    if (!session || !user?.id) {
      setSupabase(null);
      setIsClientReady(true);
      clientInitializedRef.current = false;
      return;
    }

    // Get the access token from the session
    const getAccessToken = async () => {
      if (
        (clientInitializedRef.current && supabase) ||
        clientCreationInFlightRef.current
      )
        return;
      clientCreationInFlightRef.current = true;
      try {
        // Prevent concurrent creations in StrictMode/HMR by using a window-scoped lock
        if (typeof window !== "undefined") {
          if (window.__ECP_CLIENT_CREATING__) {
            clientCreationInFlightRef.current = false;
            return;
          }
          window.__ECP_CLIENT_CREATING__ = true;
        }
        // StrictMode-safe: if a browser singleton already exists, reuse it without re-creating
        if (typeof window !== "undefined") {
          if (window.__ECP_SUPABASE_CLERK_CLIENT__) {
            setSupabase(
              window.__ECP_SUPABASE_CLERK_CLIENT__ as ReturnType<
                typeof createClerkSupabaseClient
              >,
            );
            setIsClientReady(true);
            clientInitializedRef.current = true;
            clientCreationInFlightRef.current = false;
            return;
          }
        }
        // Use Clerk's default JWT (natural integration)
        const token = await session.getToken();
        if (token) {
          // Avoid recreating client if token unchanged and client exists
          if (lastTokenRef.current === token && supabase) {
            setIsClientReady(true);
            clientInitializedRef.current = true;
          } else {
            const client = createClerkSupabaseClient({ accessToken: token });
            setSupabase(client);
            setIsClientReady(true);
            clientInitializedRef.current = true;
            lastTokenRef.current = token;
          }
        } else {
          logger.warn("🔍 [ORG CONTEXT] No access token available");
          setSupabase(null);
          setIsClientReady(true);
          clientInitializedRef.current = false;
        }
      } catch (error) {
        logger.error("🔍 [ORG CONTEXT] Failed to get access token:", error);
        setSupabase(null);
        setIsClientReady(true);
        clientInitializedRef.current = false;
      } finally {
        if (typeof window !== "undefined") {
          window.__ECP_CLIENT_CREATING__ = false;
        }
        clientCreationInFlightRef.current = false;
      }
    };

    getAccessToken();
  }, [session, user?.id, supabase, isTestMode]);

  // Track last logged auth state to avoid redundant logs
  const lastAuthStateRef = useRef<{
    hasUser: boolean;
    userId: string | undefined;
    hasSession: boolean;
    orgMemberships: number;
  } | null>(null);

  // Optimized organization data fetching - NO API CALLS
  useEffect(() => {
    if (isTestMode) return; // Skip in test mode

    const getOrganizationData = async () => {
      // Only log auth state changes (not on every render)
      const currentAuthState = {
        hasUser: !!user?.id,
        userId: user?.id,
        hasSession: !!session,
        orgMemberships: user?.organizationMemberships?.length ?? 0,
      };
      const lastAuthState = lastAuthStateRef.current;

      if (
        process.env.NODE_ENV === "development" &&
        (!lastAuthState ||
          lastAuthState.hasUser !== currentAuthState.hasUser ||
          lastAuthState.userId !== currentAuthState.userId ||
          lastAuthState.hasSession !== currentAuthState.hasSession ||
          lastAuthState.orgMemberships !== currentAuthState.orgMemberships)
      ) {
        logger.info(" [DEBUG] Auth state:", {
          ...currentAuthState,
          sessionId: session?.id,
          primaryOrgId: user?.organizationMemberships?.[0]?.organization?.id,
        });
        lastAuthStateRef.current = currentAuthState;
      }

      if (!user?.id || !session) {
        setOrganizationId(null);
        setIsLoading(false);
        hasFetchedOrg.current = false;
        // Only clear caches on actual session loss (not initial mount when Clerk is loading)
        if (hadSessionRef.current) {
          clearTokenCache();
          clearOrgCache();
          clearThemeFromDOM();
          clearOrgThemeCookie();
          clearOnboardingDraft();
          clearCountryCache();
          // Reset density so marketing/auth pages use default sizing
          if (typeof document !== "undefined") {
            document.documentElement.style.removeProperty("font-size");
          }
          logger.info(
            "🧹 [ORG CONTEXT] Cleared all user caches on session loss",
          );
        }
        return;
      }

      // Mark that we've seen a valid session
      hadSessionRef.current = true;

      const cached = getOrgFromCache(user.id);
      if (cached !== null) {
        logger.info("🔍 [ORG CONTEXT] Using cached organization data");
        setOrganizationId(cached);
        setIsLoading(false);
        hasFetchedOrg.current = true;
        return;
      }

      // Prevent duplicate requests
      if (hasFetchedOrg.current) {
        return;
      }

      setIsLoading(true);
      hasFetchedOrg.current = true;

      try {
        // Get organization directly from user memberships (NO API CALL)
        const orgId = user?.organizationMemberships?.[0]?.organization?.id;

        if (orgId) {
          setOrganizationId(orgId);
          setOrgInCache(user.id, orgId);
          logger.info(
            "🔍 [ORG CONTEXT] Found organization from Clerk session:",
            { orgId },
          );
        } else {
          setOrganizationId(null);
          logger.info(
            "🔍 [ORG CONTEXT] No organization found in Clerk session",
          );
        }
      } catch (error) {
        logger.error("🔍 [ORG CONTEXT] Failed to get organization:", error);
        setOrganizationId(null);
      } finally {
        setIsLoading(false);
      }
    };

    getOrganizationData();
  }, [user?.id, session, user?.organizationMemberships, isTestMode]);

  // NOTE: User creation and organization linking is handled by Clerk webhooks
  // (see src/app/api/webhook/clerk/route.ts). The /api/fix-user endpoint is a
  // fallback for edge cases and should only be called reactively when there's
  // an actual error (e.g., 404 when fetching user data), not proactively on
  // every page load. This prevents unnecessary API calls and duplicate requests.

  const effectiveUserId = userId ?? (isTestMode ? "test-user-12345" : null);
  const isAuthenticated = !!sessionId || isTestMode;

  // Split context values for granular re-renders
  const identityValue: OrgIdentityContextType = useMemo(
    () => ({
      organizationId,
      isLoading,
      isAuthenticated,
      userId: effectiveUserId,
      organizationCountry,
    }),
    [
      organizationId,
      isLoading,
      isAuthenticated,
      effectiveUserId,
      organizationCountry,
    ],
  );

  const clientValue: OrgClientContextType = useMemo(
    () => ({
      supabase,
      isClientReady,
    }),
    [supabase, isClientReady],
  );

  // Legacy combined value for backward compatibility
  const value: OrganizationContextType = useMemo(
    () => ({
      ...identityValue,
      ...clientValue,
    }),
    [identityValue, clientValue],
  );

  return (
    <OrgIdentityContext.Provider value={identityValue}>
      <OrgClientContext.Provider value={clientValue}>
        <OrganizationContext.Provider value={value}>
          {children}
        </OrganizationContext.Provider>
      </OrgClientContext.Provider>
    </OrgIdentityContext.Provider>
  );
}

// Test mode provider that doesn't use Clerk hooks
function OrganizationProviderTestMode({
  children,
  testOrgId,
}: {
  children: React.ReactNode;
  testOrgId: string | null;
}) {
  const [organizationId] = useState<string | null>(testOrgId);
  const [isLoading] = useState(false);
  const [isClientReady] = useState(true);
  const [supabase, setSupabase] = useState<ReturnType<
    typeof createClerkSupabaseClient
  > | null>(null);
  const [organizationCountry] = useState<string | null>("US");

  useEffect(() => {
    // Create a mock Supabase client for test mode
    try {
      const mockClient = createClerkSupabaseClient({
        accessToken: "test-token",
      });
      setSupabase(mockClient);
    } catch (error) {
      logger.warn(
        "🧪 [ORG CONTEXT] Could not create test Supabase client:",
        error,
      );
    }
  }, []);

  const identityValue: OrgIdentityContextType = useMemo(
    () => ({
      organizationId,
      isLoading,
      isAuthenticated: true,
      userId: "test-user-12345",
      organizationCountry,
    }),
    [organizationId, isLoading, organizationCountry],
  );

  const clientValue: OrgClientContextType = useMemo(
    () => ({
      supabase,
      isClientReady,
    }),
    [supabase, isClientReady],
  );

  const value: OrganizationContextType = useMemo(
    () => ({
      ...identityValue,
      ...clientValue,
    }),
    [identityValue, clientValue],
  );

  return (
    <OrgIdentityContext.Provider value={identityValue}>
      <OrgClientContext.Provider value={clientValue}>
        <OrganizationContext.Provider value={value}>
          {children}
        </OrganizationContext.Provider>
      </OrgClientContext.Provider>
    </OrgIdentityContext.Provider>
  );
}

// Test mode detection - same logic as RootProviders
// Uses ONLY environment variables (works in SSR and client)
// This must be checked BEFORE rendering to avoid Clerk hook errors
// Note: We check window globals in a separate client-side check
const isTestModeActive = (): boolean => {
  // Environment-based check (works in SSR)
  const envTestMode =
    process.env["NEXT_PUBLIC_E2E_TEST_MODE"] === "true" ||
    process.env["E2E_TEST_MODE"] === "true" ||
    process.env["NODE_ENV"] === "test";

  // Client-side check (only if window is available)
  if (typeof window !== "undefined") {
    return envTestMode || Boolean(window.__TEST_BYPASS_ENABLED);
  }

  return envTestMode;
};

// Public provider that conditionally renders based on test mode
export function OrganizationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Check test mode using environment variables (works in SSR and client)
  // This MUST be checked before any Clerk hooks are called
  // We use a function call (not a hook) to check test mode
  const testModeActive = isTestModeActive();

  // In test mode, render test-only provider (no Clerk hooks)
  // We get test org ID from window globals (set by test bypass), environment, or default
  if (testModeActive) {
    // Get test org ID - try window globals first (from test bypass setup), then environment, then default
    let testOrgId: string | null = null;

    if (typeof window !== "undefined") {
      testOrgId = window.__TEST_ORG_ID ?? null;
    }

    // Fallback to environment variables if window global not set
    if (!testOrgId) {
      testOrgId =
        process.env["TEST_ORGANIZATION_ID"] ??
        process.env["NEXT_PUBLIC_TEST_ORGANIZATION_ID"] ??
        null;
    }

    // Final fallback to default test user org ID
    if (!testOrgId) {
      testOrgId = DEFAULT_TEST_USERS.owner.organizationId;
    }

    return (
      <OrganizationProviderTestMode testOrgId={testOrgId}>
        {children}
      </OrganizationProviderTestMode>
    );
  }

  // In normal mode, render provider that uses Clerk hooks
  // This is safe because ClerkProvider wraps this in RootProviders
  return (
    <OrganizationProviderInternal isTestMode={false} testOrgId={null}>
      {children}
    </OrganizationProviderInternal>
  );
}

/**
 * Full context — backward compatible. Use useOrgIdentity() or useOrgClient()
 * when you only need a subset, to avoid unnecessary re-renders.
 */
export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  }
  return context;
}

/**
 * PERF: Only re-renders when org identity changes (org switch, auth state).
 * Does NOT re-render on Supabase client token refresh.
 * Use this when you only need organizationId, userId, isLoading, etc.
 */
export function useOrgIdentity() {
  const context = useContext(OrgIdentityContext);
  if (!context) {
    throw new Error(
      "useOrgIdentity must be used within an OrganizationProvider",
    );
  }
  return context;
}

/**
 * PERF: Only re-renders when the Supabase client changes (token refresh).
 * Does NOT re-render on org identity changes.
 * Use this when you only need the supabase client instance.
 */
export function useOrgClient() {
  const context = useContext(OrgClientContext);
  if (!context) {
    throw new Error("useOrgClient must be used within an OrganizationProvider");
  }
  return context;
}
